import * as React from 'react'
import { useContext, useMemo, useState } from 'react'
import { makeStyles } from '@fluentui/react-components'
import { AddFilled, AddRegular, bundleIcon } from '@fluentui/react-icons'
import { useTranslation } from 'react-i18next'
import useAxios from '../../../hooks/useAxios'
import { BaseOverviewCount, BaseOverviewLineItemProps, parseFilterValuesToParams, useOverviewStyles } from '../../../pages/BaseOverview/BaseOverview'
import { BaseOverviewFilterProps, BaseOverviewFilterType } from '../../../pages/BaseOverview/BaseOverviewFilter'
import { BaseOverviewSortingType } from '../../../pages/BaseOverview/BaseSortMenu'
import BaseMutableOverview, {
    BaseMutableOverviewActionButtonProps,
    BaseMutableOverviewProps,
} from '../../../pages/BaseMutableOverview/BaseMutableOverview'
import { mapNoteOverviewDtoToMutation, NoteMutationDto, NoteOverviewDto } from '../../../config/noteConfig'
import NoteHeaderItem from './NoteHeaderItem'
import NoteListItem from './NoteListItem'
import NoteMutationDialogBody from '../../components/Dialog/Note/NoteMutationDialogBody'
import TooltippedButton from '../../components/Button/TooltippedButton'
import FileContext from '../../../context/FileContext'

const AddIcon = bundleIcon(AddFilled, AddRegular)

export const useItemStyles = makeStyles({
    // A note is a page from a notebook, not a post-it: about A5 wide instead of the 874px the other
    // overviews use. This class lands after styles.list in mergeClasses, so it wins. On a narrow screen
    // that list is already 100% wide, so there is nothing to override there.
    list: {
        width: 'min(620px, 100%)',
    },
    listNoteItem: {
        // 1fr, not auto: the pin and the chevron have to sit against the right edge whatever the title is.
        gridTemplateColumns: '1fr min-content',
        gridTemplateRows: 'auto auto',
    },
})

export type NoteFilterType = BaseOverviewFilterType & {
    files?: string[]
}

export type NoteSortingType = BaseOverviewSortingType<{ id: string }>

export type NoteOverviewProps = BaseMutableOverviewProps<NoteOverviewDto, NoteFilterType, NoteSortingType, BaseOverviewCount>

export type BaseNoteOverviewProps = Partial<NoteOverviewProps> & {
    baseUrl: string
    fileId: string
}

export type BaseNoteLineOverviewProps = BaseNoteOverviewProps &
    BaseOverviewLineItemProps<NoteOverviewDto, NoteFilterType, NoteSortingType> & {
        reloadItems?: () => void
    }

function HeaderActionsButtons(
    props: BaseOverviewFilterProps<
        NoteOverviewDto,
        NoteFilterType,
        NoteSortingType,
        BaseOverviewCount,
        BaseMutableOverviewActionButtonProps<NoteOverviewDto>
    >,
) {
    const { t: translate } = useTranslation()

    return (
        <TooltippedButton
            display={'iconOnly'}
            appearance={'primary'}
            label={translate('file.pivot.note.action.create')}
            icon={<AddIcon />}
            onClick={() => props.setOpenMutationDialog(true, null)}
        />
    )
}

/**
 * The notes of a dossier as a list (HAM-3476). The order is fixed - pinned first, then newest first on creation
 * date - so there is no sort menu, and every note belongs to this dossier, so there is nothing to filter on
 * either: only the search box of BaseOverview is left, which searches title and description server side.
 */
export default function BaseNoteOverview(props: BaseNoteOverviewProps) {
    const axios = useAxios()
    const itemStyles = useItemStyles()
    const overviewStyles = useOverviewStyles()
    const { setNoteCount } = useContext(FileContext)

    // Pinning reorders the whole list, so it asks for a reload instead of updating the single item.
    const [pinReloadTrigger, setPinReloadTrigger] = useState<number>(0)

    const defaultFilterValues = useMemo<NoteFilterType>(
        () => ({
            query: '',
            files: props.fileId ? [props.fileId] : undefined,
            ...props.defaultFilterValues,
        }),
        [props.fileId, props.defaultFilterValues],
    )

    const countItems = (_column?: number, filterValues?: NoteFilterType): Promise<BaseOverviewCount> =>
        axios.get('/note/count', { params: parseFilterValuesToParams(filterValues) }).then(response => {
            // The tab indicator has to follow adding and deleting without reloading the dossier (AC 1), and the
            // overview already asks for this count on every reload. Only while nothing is searched for: with a
            // query this counts the hits, and the tab would claim the dossier has fewer notes than it has.
            if (!filterValues?.query) {
                setNoteCount?.(response.data?.total)
            }
            return response.data
        })

    const loadItems = (
        _column?: number,
        filterValues?: NoteFilterType,
        sortingValues?: NoteSortingType,
        start?: number,
        size?: number,
    ): Promise<NoteOverviewDto[]> =>
        axios.get('/note', { params: parseFilterValuesToParams(filterValues, sortingValues, start, size) }).then(response => response?.data || [])

    return (
        <BaseMutableOverview<
            NoteOverviewDto,
            NoteFilterType,
            NoteSortingType,
            BaseOverviewCount,
            BaseMutableOverviewActionButtonProps<NoteOverviewDto>,
            any,
            NoteMutationDto
        >
            defaultFilterValues={defaultFilterValues}
            countItems={countItems}
            loadItems={loadItems}
            listItemHeaderContent={NoteHeaderItem}
            listItemHeaderClassnames={() => [overviewStyles.listBaseHeaderItem]}
            listItemContent={(itemProps: BaseNoteLineOverviewProps) => (
                <NoteListItem {...props} {...itemProps} reloadItems={() => setPinReloadTrigger(trigger => trigger + 1)} />
            )}
            listClassnames={[itemStyles.list]}
            listItemClassnames={() => [itemStyles.listNoteItem]}
            headerActionsButtons={HeaderActionsButtons}
            inputPlaceholderKey={'file.pivot.note.filter.search'}
            listEmptyPlaceholderKey={'file.pivot.note.overview.empty'}
            mutationDialogFormProps={item => ({
                // The body keeps rendering while the dialog animates shut, with the item already gone (HAM-4305).
                initialValues: item ? mapNoteOverviewDtoToMutation(item, props.fileId) : { fileId: props.fileId, title: '', description: undefined },
                onSubmit: () => {},
            })}
            mutationDialogBodyProps={item => ({ item })}
            mutationDialogBody={NoteMutationDialogBody}
            mutationDialogProps={{ warnOnUnsavedChanges: true }}
            {...props}
            reloadTrigger={(Number(props.reloadTrigger) || 0) + pinReloadTrigger}
        />
    )
}
